import {isPendingStatus} from '@github-ui/mergebox/page-data/payloads/status-checks'
import {useState} from 'react'
import {Flash} from '@primer/react'
import {AlertFillIcon, StopIcon} from '@primer/octicons-react'
import {ForYouItem} from '../shared/ForYouItem'
import {StatusCheckStatesIcon} from '@github-ui/mergebox/components/sections/checks/StatusCheckStatesIcon'
import {ApproveWorkflowsButton} from '@github-ui/mergebox/components/sections/checks/ApproveWorkflowsButton'
import {WorkflowApprovalSubtitle} from '@github-ui/mergebox/components/sections/checks/WorkflowApprovalSubtitle'
import {ChecksSection} from './shared/ChecksSection'
import {useValidatedChecksData} from './use-validated-checks-data'
import {
  accessibleChecksSummary,
  checksSectionHeading,
} from '@github-ui/mergebox/components/sections/checks/checks-section-header-text'
import styles from '../shared/ForYouItem.module.css'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'

// Renders when any checks are pending or workflow runs are awaiting approval
// It will show all check states - the equivalent in the full mergebox when any checks are pending
export function PendingChecks() {
  const data = useValidatedChecksData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  if (!data) {
    return null
  }

  const {mergeBoxPullRequest, statusChecks, statusRollup, checksSection} = data
  const hasPendingChecks = statusRollup.summary.some(check => isPendingStatus(check.state))
  const workflowApproval = statusRollup.pendingWorkflowApprovalRollup
  const isPendingApproval = statusRollup.combinedState === 'PENDING_APPROVAL'

  if (!hasPendingChecks && !isPendingApproval) {
    return null
  }

  const userCanApproveWorkflowRuns = workflowApproval?.viewerCanApproveWorkflowRuns ?? false

  return (
    <ForYouItem
      leadingVisual={
        isPendingApproval ? (
          <ForYouLeadingIcon icon={AlertFillIcon} variant="warning" />
        ) : (
          <StatusCheckStatesIcon statusRollupSummary={statusRollup.summary} />
        )
      }
      title={checksSectionHeading[checksSection.sectionStatus]}
      itemName="pending-checks"
      padding="none"
      defaultOpen={isPendingApproval && userCanApproveWorkflowRuns}
      description={
        isPendingApproval && workflowApproval ? (
          <WorkflowApprovalSubtitle pendingWorkflowApprovalRollup={workflowApproval} className={styles.description} />
        ) : (
          accessibleChecksSummary(statusRollup.summary)
        )
      }
      actionSlot={
        isPendingApproval && userCanApproveWorkflowRuns ? (
          <ApproveWorkflowsButton
            headSha={mergeBoxPullRequest.headRefOid}
            onApprovalStart={() => setErrorMessage(null)}
            onError={setErrorMessage}
          />
        ) : undefined
      }
    >
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}

      {statusChecks.length === 0 ? (
        <Flash className="tmp-m-3" variant="default">
          {isPendingApproval
            ? 'Workflow runs are awaiting approval. Check results will appear once the workflows begin running.'
            : 'No checks have been reported for this pull request yet.'}
        </Flash>
      ) : (
        <ChecksSection
          statusChecks={statusChecks}
          mergeBoxPullRequest={mergeBoxPullRequest}
          statusRollup={statusRollup}
        />
      )}
    </ForYouItem>
  )
}
